import type { EnvironmentThreadShell } from "@cleo/client-runtime/state/models";
import type { ThreadId } from "@cleo/contracts";
import { useNavigate } from "@tanstack/react-router";
import { useCallback } from "react";
import { fetchPrimary } from "../environments/primary/fetch";

import { openWorldSession } from "./worldSessionClient";
import { randomUUID } from "../lib/utils";
import { toastManager } from "../components/ui/toast";
import { recordWorldSessionBirthAttempt } from "./worldSessionBirthAttemptsClient";
import { prepareSiblingWorldSessionExecutionBirth } from "./worldSessionExecutionBirth";
import { useWorldSessionExecutionDispatch } from "./useWorldSessionLaunchContext";
import { useSessionFork } from "./useSessionFork";

/**
 * SR-CAN-03/BD4 — New thread retires, then begins again on the same slice;
 * Fork delegates to the sibling-World-Session door without retiring. The standing
 * session retires through the SR-CAN-02 door FIRST. Already-retired counts
 * as standing; every other refusal is visible and blocks the continuation,
 * because New thread may never leave two standing executions while claiming
 * the previous one retired. Then the
 * sibling prepare mints the new thread on the SAME slice, the attempt is
 * recorded durably, thread.create dispatches, and the room addresses the
 * slice — the recency law fronts the new session.
 */
export interface SessionSiblingBirthInput {
  readonly worldKey: string;
  readonly sessionId: string;
  readonly sessionLabel: string;
  readonly source: EnvironmentThreadShell;
  readonly lineage: "fresh" | "fork" | "sub";
}

/** True when the retirement record STANDS — freshly written, or already
 * standing from an earlier boundary moment (already-retired). Exported for
 * the Room Rail's own retire verb (RM-CAN-10 law 8): the SR-CAN-02 door,
 * then NV2's headless engagement — two existing doors, one fold. */
async function retainStandingTranscript(threadId: ThreadId): Promise<string | null> {
  try {
    const response = await fetchPrimary("/api/neucleos/session/retire", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ threadId }),
    });
    const body: unknown = await response.json();
    const record =
      typeof body === "object" && body !== null ? (body as Record<string, unknown>) : {};
    const memberId = record["memberId"];
    const segments = typeof memberId === "string" ? memberId.split("/") : [];
    const validMember =
      segments.length === 5 &&
      segments[0] === "world-r2:context" &&
      segments[1] === "sessions" &&
      /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/.test(segments[2] ?? "") &&
      /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/.test(segments[3] ?? "") &&
      segments[4] === `${threadId}.md`;
    if (response.ok && record["ok"] === true && validMember) {
      toastManager.add({
        type: "success",
        title: "Conversation saved to the World",
        description: String(memberId),
      });
      return String(memberId);
    }
    // The boundary moment is one-shot — a record that already stands is
    // exactly what we wanted, so already-retired stays quiet.
    if (response.status === 409 && record["reason"] === "already-retired" && validMember) {
      return String(memberId);
    }
    toastManager.add({
      type: "warning",
      title: "The session could not retire into the boundary",
      description: `${typeof record["reason"] === "string" ? record["reason"] : "retirement-unavailable"} — no new thread was opened.`,
    });
    return null;
  } catch {
    toastManager.add({
      type: "warning",
      title: "The session could not retire into the boundary",
      description: "The retire door did not answer, so no new thread was opened.",
    });
    return null;
  }
}

export async function retireStandingSession(threadId: ThreadId): Promise<boolean> {
  return (await retainStandingTranscript(threadId)) !== null;
}

export function useSessionSiblingBirth(): (
  input: SessionSiblingBirthInput,
) => Promise<ThreadId | null> {
  const navigate = useNavigate();
  const { dispatch, executionArrived } = useWorldSessionExecutionDispatch();
  const forkSession = useSessionFork();
  return useCallback(
    async ({ worldKey, sessionId, sessionLabel, source, lineage }) => {
      if (lineage === "fork") {
        return forkSession({ worldKey, sourceSessionId: sessionId, source });
      }
      if (lineage === "sub") {
        // A child is born through the ordinary session door: its room board's
        // declared tools, narrowed by reach and the grant, with only the World's
        // declared folders selected. Extra tools and content require deliberate
        // selection after birth.
        const childId = `S_${randomUUID().replaceAll("-", "")}`;
        const born = await openWorldSession({
          worldKey,
          sessionId: childId,
          label: `Sub-session · ${sessionLabel}`,
          parentId: sessionId,
        });
        if (!born.ok) {
          toastManager.add({
            type: "error",
            title: "The sub-session was not born",
            description: born.reason,
          });
          return null;
        }
        void navigate({ to: "/session/$sessionId", params: { sessionId: childId } } as never);
        return null;
      }
      // (1) The boundary moment: the standing session retires first.
      const transcriptMemberId = await retainStandingTranscript(source.id);
      if (transcriptMemberId === null) return null;
      // (2) The sibling prepare — same slice, copied execution fields.
      const prepared = prepareSiblingWorldSessionExecutionBirth({
        worldKey,
        sessionId,
        label: `New thread · ${sessionLabel}`,
        source,
      });
      if (prepared._tag === "Refused") {
        toastManager.add({
          type: "error",
          title: "The new session could not start",
          description:
            prepared.reason === "source-execution-required"
              ? "The slice's standing session is not available."
              : prepared.reason === "source-coordinate-mismatch"
                ? "The standing session does not match this slice."
                : "The slice address is invalid.",
        });
        return null;
      }
      // (3) JB1: the identity pair becomes a durable server-side intent
      // before dispatch. Refuse an untracked continuation: otherwise a
      // reload could mint another identity for the same boundary moment.
      const reserved = await recordWorldSessionBirthAttempt({
        worldKey,
        sessionId,
        environmentId: prepared.environmentId,
        input: prepared.input,
      });
      if (!reserved.ok) {
        toastManager.add({
          type: "error",
          title: "The new session could not start",
          description: "It couldn't be set up. Try again.",
        });
        return null;
      }
      const outcome = await dispatch(prepared.environmentId, prepared.input);
      // The direct thread route is only a valid World address once the local
      // projection contains the newborn shell. Reconcile arrival even when
      // dispatch lost its acknowledgement, then navigate.
      const arrived = await executionArrived(prepared.environmentId, prepared.input.threadId);
      if (!arrived) {
        toastManager.add({
          type: "error",
          title: "The new session was not created",
          description: outcome.ok
            ? "The new session is recorded but hasn't appeared yet."
            : "It couldn't be started. The earlier conversation is saved; try again.",
        });
        return null;
      }
      // (4) Address the slice only after the newborn shell arrived. The
      // Session's recency projection can now front the new execution without
      // racing the direct-thread route guard's aggregate shell subscription.
      void navigate({
        to: "/session/$sessionId",
        params: { sessionId },
      } as never);
      return prepared.input.threadId;
    },
    [dispatch, executionArrived, forkSession, navigate],
  );
}
